import type { Action } from '@atlassian/react-sweet-state';
import type { State } from './types.tsx';

export const actions = {
	resize:
		(col: number, delta: number, commit = false): Action<State> =>
		({ setState, getState }) => {
			const { widths, onWidthsChange, resizer } = getState();

			if (!resizer) {
				return;
			}

			const preview = resizer({ column: col, delta })(widths);

			if (commit) {
				onWidthsChange(preview);
			}

			setState({ preview: commit ? undefined : preview });
		},

	setWidths:
		(next: number[]): Action<State> =>
		({ setState }) =>
			setState({ widths: next }),

	setFocusVisible:
		(visible: boolean): Action<State> =>
		({ setState, getState }) => {
			const { focus } = getState();

			if (focus.visible !== visible) {
				setState({ focus: { ...focus, visible } });
			}
		},

	navigate:
		(
			e: KeyboardEvent,
			direction: 'forward' | 'backward',
			axis: 'horizontal' | 'vertical' | 'auto',
			step: 'single' | 'multiple' | 'all' = 'single',
		): Action<State> =>
		({ getState, setState }) => {
			const { focus, rows, columns } = getState();
			if (!focus.visible) {
				return;
			}

			/** Returns a valid column/row number if input is outside of min and max. */
			const within = (min: number, max: number) => (input: number) =>
				Math.max(min, Math.min(input, max));

			const update =
				(value: (curr: number) => number) =>
				(curr: State['focus']): State['focus'] => {
					const finalAxis = (() => {
						if (axis !== 'auto') return axis;
						if (focus.column === -1) return 'vertical';
						if (e.ctrlKey) return 'vertical';

						return 'horizontal';
					})();

					switch (finalAxis) {
						case 'vertical':
							return {
								...curr,
								row: within(0, rows.length - 1)(value(focus.row)),
							};

						case 'horizontal':
							return {
								...curr,
								column: within(-1, columns.length - 1)(value(focus.column)),
							};

						default:
							return curr;
					}
				};

			const setFocus = (updateFn: (curr: State['focus']) => State['focus']) => {
				e.preventDefault();
				setState({ focus: updateFn(focus) });
			};

			if (direction === 'forward') {
				if (step === 'single') {
					setFocus(update((x) => x + 1));
				}
				if (step === 'multiple') {
					setFocus(update((x) => x + 5));
				}
				if (step === 'all') {
					setFocus(update(() => rows.length - 1));
				}
			}

			if (direction === 'backward') {
				if (step === 'single') {
					setFocus(update((x) => x - 1));
				}
				if (step === 'multiple') {
					setFocus(update((x) => x - 5));
				}
				if (step === 'all') {
					setFocus(update(() => 0));
				}
			}
		},
};
